import { useAuth0 } from '@auth0/auth0-react';
import { ConfirmDialog } from '@ths/ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';

import { rememberClient } from './rememberedClient';
import {
  clientFromPath,
  companySwitchDestination,
  useHotelScope,
} from './scope';

type Confirmation = {
  stateKey: string;
  actorId: string;
  clientId: string;
  generation: number;
};
type HotelSession = {
  requestSignOut: (action: () => Promise<void>) => void;
  accessRevision: number;
  confirmation: Confirmation | null;
  confirm: (clientId: string) => void;
  requestCompanySwitch: (clientId: string) => void;
  registerChange: (scopeKey: string, source: string, dirty: boolean) => void;
  mutationActivity: (delta: number) => void;
  invalidate: () => void;
};
const HotelSessionContext = createContext<HotelSession | null>(null);
export const useOptionalHotelSession = () => useContext(HotelSessionContext);
export function useHotelSession() {
  const value = useContext(HotelSessionContext);
  if (!value) throw new Error('Hotel session is unavailable');
  return value;
}

/** Each mounted editor owns its registration; old scopes cannot alter new ones. */
export function useHotelChangeRegistration() {
  const register = useOptionalHotelSession()?.registerChange;
  const stateKey = useHotelScope()?.stateKey;
  const source = useId();
  const update = useCallback(
    (dirty: boolean) => {
      if (stateKey) register?.(stateKey, source, dirty);
    },
    [register, source, stateKey],
  );
  useEffect(() => () => update(false), [update]);
  return update;
}

export function useHotelUnsavedChanges(dirty: boolean) {
  const register = useHotelChangeRegistration();
  useEffect(() => register(dirty), [dirty, register]);
}

export function HotelSessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocationKey = useRef(location.key);
  const { user } = useAuth0();
  const actorId = user?.sub;
  const [stored, setStored] = useState<Confirmation | null>(null);
  const [activity, setActivity] = useState({ key: '', count: 0 });
  const [changes, setChanges] = useState({
    key: '',
    sources: new Set<string>(),
  });
  const [switchTarget, setSwitchTarget] = useState<string | null>(null);
  const approvedClient = useRef<string | null>(null);
  const generation = useRef(0);
  const [epoch] = useState(() => crypto.randomUUID());
  const [signOutAction, setSignOutAction] = useState<
    (() => Promise<void>) | null
  >(null);
  const [accessRevision, setAccessRevision] = useState(0);
  const confirmation = stored?.actorId === actorId ? stored : null;
  const activityKey = confirmation?.stateKey ?? '';
  const activeKey = useRef(activityKey);
  activeKey.current = activityKey;
  const pending = activity.key === activityKey ? activity.count : 0;
  const hasUnsavedChanges =
    changes.key === activityKey && changes.sources.size > 0;
  useEffect(() => {
    setStored(null);
    setSignOutAction(null);
    setSwitchTarget(null);
    approvedClient.current = null;
  }, [actorId]);
  useEffect(() => {
    if (previousLocationKey.current === location.key) return;
    previousLocationKey.current = location.key;
    approvedClient.current = null;
    if (
      confirmation &&
      clientFromPath(location.pathname) !== confirmation.clientId
    )
      setStored(null);
  }, [location.key, location.pathname, confirmation]);
  const confirm = useCallback(
    (clientId: string) => {
      if (!actorId) return;
      const nextGeneration = ++generation.current;
      setStored({
        actorId,
        clientId,
        generation: nextGeneration,
        stateKey: `${epoch}:${nextGeneration}`,
      });
      rememberClient(actorId, clientId);
    },
    [actorId, epoch],
  );
  const completeSwitch = useCallback(
    (clientId: string) => {
      // Continue in the picker is the explicit confirmation for this destination.
      approvedClient.current = clientId;
      confirm(clientId);
      setSwitchTarget(null);
      navigate(companySwitchDestination(clientId, location));
    },
    [confirm, navigate, location],
  );
  const requestCompanySwitch = useCallback(
    (clientId: string) => {
      if (clientId === confirmation?.clientId) return;
      if (pending || hasUnsavedChanges) setSwitchTarget(clientId);
      else completeSwitch(clientId);
    },
    [confirmation?.clientId, pending, hasUnsavedChanges, completeSwitch],
  );
  const registerChange = useCallback(
    (scopeKey: string, source: string, dirty: boolean) => {
      if (activeKey.current !== scopeKey) return;
      setChanges((previous) => {
        const sources =
          previous.key === scopeKey ? previous.sources : new Set<string>();
        if (sources.has(source) === dirty) return previous;
        const next = new Set(sources);
        if (dirty) next.add(source);
        else next.delete(source);
        return { key: scopeKey, sources: next };
      });
    },
    [],
  );
  const mutationActivity = useCallback(
    (delta: number) => {
      if (activeKey.current !== activityKey) return;
      setActivity((previous) => ({
        key: activityKey,
        count: Math.max(
          0,
          (previous.key === activityKey ? previous.count : 0) + delta,
        ),
      }));
    },
    [activityKey],
  );
  const requestSignOut = useCallback(
    (action: () => Promise<void>) => {
      if (!hasUnsavedChanges && !pending) {
        setStored(null);
        void action();
        return;
      }
      setSignOutAction(() => action);
    },
    [hasUnsavedChanges, pending],
  );
  const invalidate = useCallback(() => {
    setStored(null);
    setSwitchTarget(null);
    setSignOutAction(null);
    setAccessRevision((revision) => revision + 1);
  }, []);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    Boolean(
      confirmation &&
        (pending > 0 || hasUnsavedChanges) &&
        (!approvedClient.current ||
          clientFromPath(nextLocation.pathname) !== approvedClient.current) &&
        clientFromPath(currentLocation.pathname) === confirmation.clientId &&
        clientFromPath(nextLocation.pathname) !== confirmation.clientId,
    ),
  );
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (pending > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [pending]);
  const value = useMemo(
    () => ({
      confirmation,
      confirm,
      requestCompanySwitch,
      registerChange,
      mutationActivity,
      invalidate,
      accessRevision,
      requestSignOut,
    }),
    [
      confirmation,
      confirm,
      requestCompanySwitch,
      registerChange,
      mutationActivity,
      invalidate,
      accessRevision,
      requestSignOut,
    ],
  );
  return (
    <HotelSessionContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={
          blocker.state === 'blocked' ||
          Boolean(signOutAction) ||
          Boolean(switchTarget)
        }
        onOpenChange={(open) => {
          if (!open) {
            setSignOutAction(null);
            setSwitchTarget(null);
            if (blocker.state === 'blocked') blocker.reset();
          }
        }}
        title={
          pending
            ? 'A save is still in progress'
            : signOutAction
              ? 'Sign out?'
              : 'Switch company?'
        }
        description={
          pending
            ? 'Wait for the current save to finish before leaving this company.'
            : hasUnsavedChanges
              ? 'Your unsaved changes and search selections will be discarded.'
              : 'Your save has finished. You can continue now.'
        }
        confirmLabel={
          hasUnsavedChanges
            ? signOutAction
              ? 'Discard and sign out'
              : 'Discard and continue'
            : signOutAction
              ? 'Sign out'
              : 'Continue'
        }
        loading={pending > 0}
        onConfirm={() => {
          if (!pending && signOutAction) {
            setStored(null);
            setSignOutAction(null);
            void signOutAction();
          } else if (!pending && switchTarget) {
            completeSwitch(switchTarget);
          } else if (!pending && blocker.state === 'blocked') {
            setStored(null);
            blocker.proceed();
          }
        }}
      />
    </HotelSessionContext.Provider>
  );
}
